import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { 
  UserCircle, 
  Shield, 
  Calendar, 
  CreditCard, 
  Search, 
  Video,
  ArrowRight
} from "lucide-react";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  detail: string;
  gradient: string;
}

const features: Feature[] = [
  {
    icon: <UserCircle className="w-8 h-8" />,
    title: "Player Passport",
    description: "Complete player profiles",
    detail: "Track stats, achievements, and career milestones in one digital passport that follows players throughout their journey.",
    gradient: "from-accent/20 to-accent-soft/20",
  },
  {
    icon: <Shield className="w-8 h-8" />,
    title: "Club Management",
    description: "Run your club efficiently",
    detail: "Manage teams, staff, programs, and registrations with professional tools designed for football organizations.",
    gradient: "from-accent-soft/20 to-accent/20",
  },
  {
    icon: <Calendar className="w-8 h-8" />,
    title: "Training Sessions",
    description: "Plan and schedule easily",
    detail: "Visual calendar for training sessions, attendance tracking, and progress monitoring for coaches and players.",
    gradient: "from-accent/20 to-primary/20",
  },
  {
    icon: <CreditCard className="w-8 h-8" />,
    title: "Payment Tracking",
    description: "Never lose a payment",
    detail: "Automated fee tracking, installment management, and payment reminders to keep finances organized.",
    gradient: "from-primary/20 to-accent/20",
  },
  {
    icon: <Search className="w-8 h-8" />,
    title: "Smart Scouting",
    description: "Discover talent easily",
    detail: "AI-powered recommendations based on player performance data and trending statistics.",
    gradient: "from-accent/20 to-accent-soft/20",
  },
  {
    icon: <Video className="w-8 h-8" />,
    title: "AI Camera",
    description: "Auto-record matches",
    detail: "Automated match recording with intelligent player tracking and instant highlight generation.",
    gradient: "from-accent-soft/20 to-accent/20",
  },
];

interface FeatureCardProps {
  feature: Feature;
  index: number;
  isInView: boolean;
}

const FeatureCard = ({ feature, index, isInView }: FeatureCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 20;
    const y = (e.clientY - rect.top - rect.height / 2) / 20;
    setMousePosition({ x, y });
  };

  return (
    <motion.div
      className="card-3d group"
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1] 
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePosition({ x: 0, y: 0 });
      }}
      onMouseMove={handleMouseMove}
      style={{
        transform: isHovered 
          ? `perspective(1000px) rotateX(${-mousePosition.y}deg) rotateY(${mousePosition.x}deg)`
          : "perspective(1000px) rotateX(0deg) rotateY(0deg)",
        transition: "transform 0.2s ease-out",
      }}
    >
      <div className={`relative h-full p-8 rounded-2xl bg-surface/80 backdrop-blur-xl border border-border/50 overflow-hidden hover-lift transition-all duration-500 hover:border-accent/50 hover:shadow-premium-lg`}>
        {/* Gradient background on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
        
        {/* Shine effect */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at ${50 + mousePosition.x * 5}% ${50 + mousePosition.y * 5}%, hsl(var(--accent) / 0.15), transparent 50%)`,
          }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Icon */}
          <motion.div 
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 border border-accent/30 mb-6 text-accent group-hover:scale-110 group-hover:bg-accent/20 transition-all duration-300"
            animate={isHovered ? { rotate: [0, -5, 5, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            {feature.icon}
          </motion.div>

          {/* Title & Description */}
          <h3 className="font-heading font-bold text-xl mb-2 text-foreground group-hover:text-accent transition-colors duration-300">
            {feature.title}
          </h3>
          <p className="text-muted-foreground text-sm mb-4">
            {feature.description}
          </p>

          {/* Hidden detail that reveals on hover */}
          <motion.div
            className="overflow-hidden"
            animate={{ height: isHovered ? "auto" : 0, opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <p className="text-sm text-foreground/80 leading-relaxed pt-2 border-t border-border/50">
              {feature.detail}
            </p>
          </motion.div>

          {/* Arrow indicator */}
          <motion.div
            className="mt-4 flex items-center gap-2 text-accent text-sm font-medium"
            animate={{ x: isHovered ? 5 : 0, opacity: isHovered ? 1 : 0.7 }}
            transition={{ duration: 0.2 }}
          >
            <span>Learn more</span>
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const InteractiveFeatureGrid = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      ref={sectionRef}
      className="min-h-screen flex items-center py-12 px-6 bg-background relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [-50, 50, -50],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-accent-soft/5 blur-3xl"
          animate={{
            scale: [1, 1.15, 1],
            y: [-30, 30, -30],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            Everything You Need
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
            A complete toolkit for modern football management
          </p>
          <motion.p
            className="text-sm text-accent font-medium"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            ✨ Hover to explore features
          </motion.p>
        </motion.div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InteractiveFeatureGrid;
